const toneClassMap = {
  error: "fittr-ios-toast--error ftr-toast--error",
  warning: "fittr-ios-toast--warning ftr-toast--warning",
  success: "fittr-ios-toast--success ftr-toast--success",
  info: "fittr-ios-toast--info ftr-toast--info",
  automation: "fittr-ios-toast--automation-start ftr-toast--automation-start",
};

const variantClassMap = {
  dark: "fittr-ios-toast--dark ftr-toast--dark",
  light: "fittr-ios-toast--light ftr-toast--light",
};

const iconMap = {
  error: "fa-solid fa-triangle-exclamation",
  warning: "fa-solid fa-circle-exclamation",
  success: "fa-solid fa-circle-check",
  info: "fa-solid fa-circle-info",
  automation: "fal fa-robot",
};

const toneByType = {
  negative: "error",
  error: "error",
  positive: "success",
  success: "success",
  warning: "warning",
  info: "info",
  automation: "automation",
};

const inferTone = (options = {}) => {
  const className = String(options.classes || "");
  if (className.includes("fittr-ios-toast--error") || className.includes("ftr-toast--error")) {
    return "error";
  }
  if (className.includes("fittr-ios-toast--success") || className.includes("ftr-toast--success")) {
    return "success";
  }
  if (className.includes("fittr-ios-toast--info") || className.includes("ftr-toast--info")) {
    return "info";
  }
  if (className.includes("fittr-ios-toast--warning") || className.includes("ftr-toast--warning")) {
    return "warning";
  }
  if (className.includes("fittr-ios-toast--automation-start") || className.includes("ftr-toast--automation-start")) {
    return "automation";
  }

  const explicitTone = toneByType[options.tone] || toneByType[options.type];
  if (explicitTone) {
    return explicitTone;
  }

  const color = String(options.color || "").toLowerCase();
  if (/(negative|danger|red|error)/.test(color)) {
    return "error";
  }
  if (/(positive|success|green)/.test(color)) {
    return "success";
  }
  if (/(warning|orange|amber|yellow)/.test(color)) {
    return "warning";
  }
  if (/(info|blue|cyan|teal)/.test(color)) {
    return "info";
  }

  return "warning";
};

const getDefaultToastVariant = () => {
  if (typeof window === "undefined") {
    return "dark";
  }

  const path = window.location?.pathname || "";
  return path === "/" || path.startsWith("/home") ? "light" : "dark";
};

const inferVariant = (options = {}) => {
  const className = String(options.classes || "");
  if (className.includes("fittr-ios-toast--light") || className.includes("ftr-toast--light")) {
    return "light";
  }
  if (className.includes("fittr-ios-toast--dark") || className.includes("ftr-toast--dark")) {
    return "dark";
  }

  const explicitVariant = String(options.variant || options.appearance || options.theme || "").toLowerCase();
  if (variantClassMap[explicitVariant]) {
    return explicitVariant;
  }

  return getDefaultToastVariant();
};

const managedClassNames = new Set([
  "fittr-ios-toast",
  "ftr-toast",
  ...Object.values(toneClassMap).flatMap((className) => className.split(/\s+/)),
  ...Object.values(variantClassMap).flatMap((className) => className.split(/\s+/)),
]);

const stripToneClasses = (classes = "") => (
  String(classes)
    .split(/\s+/)
    .filter((className) => (
      className
      && !managedClassNames.has(className)
    ))
    .join(" ")
);

export const normalizeToastOptions = (options = {}) => {
  const sourceOptions = typeof options === "string" ? { message: options } : (options || {});
  const resolvedTone = toneClassMap[sourceOptions.tone]
    ? sourceOptions.tone
    : inferTone(sourceOptions);
  const resolvedVariant = inferVariant(sourceOptions);
  const customClasses = stripToneClasses(sourceOptions.classes);

  return {
    ...sourceOptions,
    position: sourceOptions.position || "center",
    timeout: sourceOptions.timeout ?? 2800,
    icon: sourceOptions.icon || iconMap[resolvedTone],
    classes: [
      "fittr-ios-toast",
      "ftr-toast",
      toneClassMap[resolvedTone],
      variantClassMap[resolvedVariant],
      customClasses,
    ].filter(Boolean).join(" "),
    color: undefined,
    textColor: undefined,
    tone: undefined,
    variant: undefined,
    appearance: undefined,
    theme: undefined,
  };
};

export const getToastOptions = ({
  message,
  tone = "info",
  icon,
  timeout = 2800,
  position = "center",
  caption,
  actions,
  variant,
  ...rest
} = {}) => {
  const resolvedTone = toneClassMap[tone] ? tone : "info";

  return normalizeToastOptions({
    position,
    timeout,
    icon: icon || iconMap[resolvedTone],
    message,
    caption,
    actions,
    variant,
    classes: [
      "fittr-ios-toast",
      "ftr-toast",
      toneClassMap[resolvedTone],
      variantClassMap[variant],
      rest.classes,
    ].filter(Boolean).join(" "),
    ...rest,
  });
};

export const notifyToast = ($q, options = {}) => {
  if (!$q?.notify) {
    return undefined;
  }

  return $q.notify(getToastOptions(options));
};

export const installGlobalToastNormalization = ($q, notifyPlugin) => {
  if (!$q?.notify || $q.notify.__fittrToastNormalized) {
    return;
  }

  const originalNotify = $q.notify.bind($q);
  const normalizedNotify = (options) => originalNotify(normalizeToastOptions(options));
  normalizedNotify.setDefaults = $q.notify.setDefaults;
  normalizedNotify.registerType = $q.notify.registerType;
  normalizedNotify.__fittrToastNormalized = true;
  $q.notify = normalizedNotify;

  if (notifyPlugin?.create && !notifyPlugin.create.__fittrToastNormalized) {
    const originalCreate = notifyPlugin.create.bind(notifyPlugin);
    notifyPlugin.create = (options) => originalCreate(normalizeToastOptions(options));
    notifyPlugin.create.__fittrToastNormalized = true;
  }
};
